/*
 * cp-trust-stream.js — Cronduit storefront live trust layer
 *
 * Shared module powering all "live" surfaces on the site:
 *   - 4 persistent counters (viewing now / orders today / orders shipped / builders)
 *   - Live activity feed (Stripe-style streaming events with rehydration)
 *   - Receipt stream ("just shipped" order ticker)
 *
 * Persistence pattern (lifted from Cronduit Newsletter.html):
 *   - EPOCH-anchored math means counter values are deterministic from time alone
 *   - localStorage cd_site_* keys hold the high-water mark so refresh never resets
 *   - Activity feed buffer rehydrates from storage; reseeds with backdated events
 *     if oldest event is >30m old (so the feed never looks frozen)
 *
 * All exports live on window.CronduitTrust so sections can opt-in by element id.
 */
(function () {
  'use strict';

  /* ─── Time anchors ─────────────────────────────────────────────────────── */
  const EPOCH = new Date('2026-04-15T00:00:00Z').getTime();
  const NOW = Date.now();
  const HOURS_SINCE = Math.max(0, (NOW - EPOCH) / 3600000);
  const startOfDay = (function () { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
  const HOURS_TODAY = (NOW - startOfDay.getTime()) / 3600000;
  const DAY_KEY = startOfDay.toISOString().slice(0, 10);

  /* ─── Storage helpers ──────────────────────────────────────────────────── */
  function getStored(key, fallback) {
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
    catch (e) { return fallback; }
  }
  function setStored(key, val) {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) { /* quota or private mode */ }
  }

  /* ─── Counter math ─────────────────────────────────────────────────────── */
  // Subscribers-on-Signal style counter, used as "builders on the stack"
  function computeBuilders() {
    const computed = 1247 + Math.floor(HOURS_SINCE * 1.4);
    return Math.max(computed, getStored('cd_site_builders_v1', 0));
  }
  // Lifetime orders shipped. Conservative floor of 120.
  function computeShipped() {
    const computed = 120 + Math.floor(HOURS_SINCE * 2.0);
    return Math.max(computed, getStored('cd_site_shipped_v1', 0));
  }
  // Orders today — resets at midnight local, grows ~3/hour into the day.
  function computeOrdersToday() {
    const computed = Math.max(2, Math.floor(HOURS_TODAY * 3));
    const stored = getStored('cd_site_orders_' + DAY_KEY, 0);
    const val = Math.max(computed, stored);
    setStored('cd_site_orders_' + DAY_KEY, val);
    return val;
  }
  // Viewing now — smooth sine wave, no storage. Refresh is coherent because t is global Date.now().
  function computeViewing() {
    const t = NOW / 60000;
    const wave = Math.sin(t / 3.7) * 22 + Math.cos(t / 2.1) * 14;
    return Math.round(178 + wave);
  }

  /* ─── Counter ticker ───────────────────────────────────────────────────── */
  function startCounters() {
    const config = [
      { id: 'cnt-viewing',      value: computeViewing(),     key: null,                       interval: [2200, 4200], view: true },
      { id: 'cnt-orders-today', value: computeOrdersToday(), key: 'cd_site_orders_' + DAY_KEY, jitter: [0, 1], interval: [9000, 22000], mono: true },
      { id: 'cnt-shipped',      value: computeShipped(),     key: 'cd_site_shipped_v1',       jitter: [0, 2], interval: [4200, 11000], mono: true },
      { id: 'cnt-builders',     value: computeBuilders(),    key: 'cd_site_builders_v1',      jitter: [0, 2], interval: [5000, 13000], mono: true },
    ];

    // Paint initial values immediately (no jump-from-zero) for every counter element on the page
    config.forEach(function (c) {
      document.querySelectorAll('[data-cp-cnt="' + c.id + '"]').forEach(function (el) {
        el.textContent = c.value.toLocaleString();
      });
    });

    config.forEach(function (c) {
      function tick() {
        let delta = 0;
        if (c.view) {
          const newV = computeViewing() + Math.floor(Math.random() * 5 - 2);
          delta = newV - c.value;
          c.value = newV;
        } else {
          delta = c.jitter[0] + Math.floor(Math.random() * (c.jitter[1] - c.jitter[0] + 1));
          c.value += delta;
          if (c.mono && c.key) setStored(c.key, c.value);
        }
        document.querySelectorAll('[data-cp-cnt="' + c.id + '"]').forEach(function (el) {
          el.textContent = c.value.toLocaleString();
          if (delta > 0) {
            el.style.transition = 'color .3s';
            el.style.color = '#00FF88';
            setTimeout(function () { el.style.color = ''; }, 600);
          }
        });
        setTimeout(tick, c.interval[0] + Math.random() * (c.interval[1] - c.interval[0]));
      }
      setTimeout(tick, 1500 + Math.random() * 2500);
    });
  }

  /* ─── Shared rel-time helper ───────────────────────────────────────────── */
  function relTimeFromAge(ageMs) {
    const s = Math.floor(ageMs / 1000);
    if (s < 5) return 'just now';
    if (s < 60) return s + 's ago';
    const m = Math.floor(s / 60);
    if (m < 60) return m + 'm ago';
    const h = Math.floor(m / 60);
    return h + 'h ago';
  }

  /* ─── Activity feed — locales (city + region-appropriate name pool) ────── */
  // Mirrors Cronduit Newsletter.html "locales" array. Keep both in sync.
  // Names are region-matched so "Marcus from Singapore" never happens; the
  // recentNames tracker blocks repeats within a 50-name session window.
  const locales = [
    // North America
    { city: 'Austin TX',     names: ['Marcus','Jordan','Riley','Hudson','Cole','Beckett','Wyatt','Sierra','Harper','Ava','Cassidy','Reese','Tatum','Brooks','Emersyn'] },
    { city: 'Brooklyn NY',   names: ['Aiden','Mason','Logan','Ezra','Mateo','Maya','Sienna','Talia','Naomi','Quincy','Asher','Jaden','Imani','Eli','Zara'] },
    { city: 'Denver CO',     names: ['Theo','Owen','Beck','Hayden','Ridge','Tess','Sage','Wren','Sawyer','Ellis','Briar','Sloan','Rhett','Juniper'] },
    { city: 'Sacramento CA', names: ['Devon','Jaylen','Mariah','Carlos','Selena','Alma','Andre','Yesenia','Tomas','Lucia','Esai','Brielle'] },
    { city: 'Nashville TN',  names: ['Wade','Colton','Sutton','Emmylou','Hank','June','Tanner','Maybelle','Boone','Cassidy','Levi','Annie','Beau','Ruthie'] },
    { city: 'Toronto',       names: ['Mason','Liam','Ethan','Olivia','Aria','Noah','Charlotte','Hayden','Quinn','Maeve','Ezra','Nolan','Avery','Sienna'] },
    { city: 'Vancouver',     names: ['Logan','Lucas','Ava','Sophie','Jasper','Wren','Kai','Harlow','Sage','Reid','Indigo','Marlow','Linden','Saoirse'] },
    // UK / Ireland
    { city: 'London',        names: ['Oliver','Harry','Charlie','Amelia','Isla','Freya','George','Arthur','Ivy','Poppy','Edward','Florence','Theo','Hugo'] },
    { city: 'Dublin',        names: ['Oisin','Cillian','Niamh','Aoife','Saoirse','Eoin','Ciara','Fionn','Roisin','Tadhg','Caoimhe','Sean','Eabha','Padraig'] },
    // Continental Europe
    { city: 'Berlin',        names: ['Lukas','Jonas','Finn','Emilia','Mia','Henrik','Klara','Mathias','Greta','Ole','Lina','Anton','Hanna','Felix'] },
    { city: 'Amsterdam',     names: ['Daan','Sem','Anneke','Maja','Bram','Lotte','Klaas','Sanne','Pieter','Femke','Joris','Iris','Thijs','Eline'] },
    { city: 'Lisbon',        names: ['Joao','Tiago','Beatriz','Mariana','Rafael','Ines','Goncalo','Carolina','Diogo','Matilde','Andre','Leonor','Margarida','Henrique'] },
    // Latin America
    { city: 'Mexico City',   names: ['Mateo','Diego','Sofia','Camila','Santiago','Valentina','Sebastian','Renata','Emiliano','Regina','Andres','Ximena','Maximiliano','Daniela'] },
    { city: 'Sao Paulo',     names: ['Lucas','Pedro','Beatriz','Larissa','Gabriel','Henrique','Isabela','Bruna','Felipe','Juliana','Murilo','Camila','Vinicius','Marcela'] },
    // Asia
    { city: 'Tokyo',         names: ['Haruto','Kenji','Sota','Aoi','Yuki','Sora','Aya','Hiro','Ren','Nao','Yui','Ayumi','Riku','Hina'] },
    { city: 'Singapore',     names: ['Wei Jin','Mei Lin','Jun Hao','Hui Min','Jia Le','Xin Yi','Ying Xuan','Kai Ming','Li Wen','Cheng Hao','Jia En','Zheng Hao','Wen Hui','Si Ying'] },
    { city: 'Manila',        names: ['Joaquin','Mateo','Liana','Aira','Andres','Camille','Iggy','Bianca','Paolo','Trisha','Migs','Yana','JM','Beatrice'] },
    { city: 'Bengaluru',     names: ['Priya','Ravi','Arjun','Anika','Vihaan','Aanya','Rohan','Meera','Aditya','Kavya','Karthik','Nisha','Sanjay','Ishita'] },
    // Pacific
    { city: 'Sydney',        names: ['Lachlan','Charlie','Indi','Holly','Jaxon','Sienna','Ollie','Mia','Hudson','Pippa','Tom','Eden','Archie','Matilda'] },
    // Africa
    { city: 'Cape Town',     names: ['Sipho','Themba','Lerato','Naledi','Andile','Zanele','Lwazi','Thandi','Kabelo','Refilwe','Bongani','Asanda','Nkosi','Karabo'] }
  ];
  const cities = locales.map(function (l) { return l.city; });
  // Recent-name tracker. 50-name window so repeats are extremely unlikely in normal browsing.
  const recentNames = [];
  const recentNamesMax = 50;
  function pickName(pool) {
    const fresh = pool.filter(function (n) { return recentNames.indexOf(n) === -1; });
    const candidates = fresh.length ? fresh : pool;
    const name = candidates[Math.floor(Math.random() * candidates.length)];
    recentNames.push(name);
    if (recentNames.length > recentNamesMax) recentNames.shift();
    return name;
  }
  const actions = [
    { ico: '◆', what: 'subscribed to Signal',         weight: 5 },
    { ico: '▸', what: 'ordered LeadPull Instant',     weight: 5 },
    { ico: '◆', what: 'paid for the Stack',           weight: 3 },
    { ico: '▸', what: 'downloaded the prompt pack',   weight: 3 },
    { ico: '◆', what: 'ran the auto-tag script',      weight: 2 },
    { ico: '▸', what: 'installed the brand canon kit',weight: 2 },
    { ico: '◆', what: 'shipped a Shopify section',    weight: 3 },
    { ico: '▸', what: 'pasted the Hero snippet',      weight: 4 },
    { ico: '◆', what: 'forwarded Signal to a teammate', weight: 2 },
    { ico: '▸', what: 'replied to drop #012',         weight: 1 },
  ];
  const weightedActions = actions.flatMap(function (a) { return Array(a.weight).fill(a); });

  function makeEvent() {
    const locale = locales[Math.floor(Math.random() * locales.length)];
    return {
      name: pickName(locale.names),
      city: locale.city,
      action: weightedActions[Math.floor(Math.random() * weightedActions.length)],
      ts: Date.now()
    };
  }
  function renderRow(ev) {
    const row = document.createElement('div');
    row.className = 'cp-feed-row';
    row.dataset.ts = ev.ts;
    row.innerHTML =
      '<span class="cp-feed-ico">' + ev.action.ico + '</span>' +
      '<span class="cp-feed-body"><span class="cp-feed-who">' + ev.name + '</span>' +
      ' from ' + ev.city +
      ' <span class="cp-feed-what">' + ev.action.what + '</span></span>' +
      '<span class="cp-feed-when">' + relTimeFromAge(Date.now() - ev.ts) + '</span>';
    return row;
  }
  function persistActivity(events) { setStored('cd_site_activity_v1', events.slice(0, 12)); }
  function loadActivity() {
    const stored = getStored('cd_site_activity_v1', []);
    return stored.map(function (e) {
      return Object.assign({}, e, { action: actions.find(function (a) { return a.what === e.action.what; }) || e.action });
    });
  }
  function startActivityFeed() {
    const list = document.getElementById('cp-activity-list');
    if (!list) return;

    let buf = loadActivity();
    if (!buf.length || (Date.now() - buf[0].ts) > 30 * 60 * 1000) {
      buf = [];
      for (let i = 0; i < 6; i++) {
        const ev = makeEvent();
        ev.ts = Date.now() - (i + 1) * Math.floor(8000 + Math.random() * 22000);
        buf.push(ev);
      }
      persistActivity(buf);
    }
    function repaint() {
      list.innerHTML = '';
      buf.slice(0, 8).forEach(function (ev) { list.appendChild(renderRow(ev)); });
    }
    function push() {
      buf.unshift(makeEvent());
      buf = buf.slice(0, 12);
      persistActivity(buf);
      list.insertBefore(renderRow(buf[0]), list.firstChild);
      while (list.children.length > 8) list.removeChild(list.lastChild);
      setTimeout(push, 1500 + Math.random() * 3500);
    }
    function refreshTimes() {
      [].slice.call(list.children).forEach(function (row) {
        const ts = parseInt(row.dataset.ts || '0', 10);
        if (ts) {
          const w = row.querySelector('.cp-feed-when');
          if (w) w.textContent = relTimeFromAge(Date.now() - ts);
        }
      });
      setTimeout(refreshTimes, 5000);
    }
    repaint();
    setTimeout(push, 1800);
    setTimeout(refreshTimes, 5000);
  }

  /* ─── Receipt stream (just-shipped order ticker) ───────────────────────── */
  const productLabels = [
    'LeadPull · Instant',
    'LeadPull · Custom',
    'LeadPull · 5-City Bundle',
    'Cold Outreach Template Pack',
    'The LeadPull Playbook',
    'AI Hero Section Kit',
    'Brand Canon Kit',
    'Auto-Tag Script',
  ];
  function makeReceipt() {
    return {
      order: 'C-' + (10000 + Math.floor(Math.random() * 89999)),
      city: cities[Math.floor(Math.random() * cities.length)],
      item: productLabels[Math.floor(Math.random() * productLabels.length)],
      ts: Date.now()
    };
  }
  function renderReceipt(r) {
    const row = document.createElement('div');
    row.className = 'cp-receipt-row';
    row.dataset.ts = r.ts;
    row.innerHTML =
      '<span class="cp-receipt-id">#' + r.order + '</span>' +
      '<span class="cp-receipt-city">' + r.city + '</span>' +
      '<span class="cp-receipt-item">' + r.item + '</span>' +
      '<span class="cp-receipt-when">' + relTimeFromAge(Date.now() - r.ts) + '</span>';
    return row;
  }
  function persistReceipts(arr) { setStored('cd_site_receipts_v1', arr.slice(0, 10)); }
  function loadReceipts() { return getStored('cd_site_receipts_v1', []); }
  function startReceiptStream() {
    const list = document.getElementById('cp-receipt-list');
    if (!list) return;
    let buf = loadReceipts();
    if (!buf.length || (Date.now() - buf[0].ts) > 30 * 60 * 1000) {
      buf = [];
      for (let i = 0; i < 5; i++) {
        const r = makeReceipt();
        r.ts = Date.now() - (i + 1) * Math.floor(15000 + Math.random() * 60000);
        buf.push(r);
      }
      persistReceipts(buf);
    }
    function repaint() {
      list.innerHTML = '';
      buf.slice(0, 6).forEach(function (r) { list.appendChild(renderReceipt(r)); });
    }
    function push() {
      buf.unshift(makeReceipt());
      buf = buf.slice(0, 10);
      persistReceipts(buf);
      list.insertBefore(renderReceipt(buf[0]), list.firstChild);
      while (list.children.length > 6) list.removeChild(list.lastChild);
      setTimeout(push, 4000 + Math.random() * 6000);
    }
    function refresh() {
      [].slice.call(list.children).forEach(function (row) {
        const ts = parseInt(row.dataset.ts || '0', 10);
        if (ts) {
          const w = row.querySelector('.cp-receipt-when');
          if (w) w.textContent = relTimeFromAge(Date.now() - ts);
        }
      });
      setTimeout(refresh, 5000);
    }
    repaint();
    setTimeout(push, 3000);
    setTimeout(refresh, 5000);
  }

  /* ─── Terminal rotator (trust hero right column) ───────────────────────── */
  function startTerminalRotator() {
    const term = document.getElementById('cp-trust-terminal');
    if (!term) return;
    const lines = [
      ['$ cronduit ship --target shopify',           '> AI hero section installed.',     '> 1 file. 3 min. 0 deps.'],
      ['$ cronduit pull --metro phoenix',            '> 2,341 verified leads.',          '> Drops in Instantly-ready CSV.'],
      ['$ cronduit ad --creative spark',             '> 12 variants generated.',         '> Meta + TikTok formats.'],
      ['$ cronduit cro --audit',                     '> 7 funnel gaps tagged.',          '> Patch playbook attached.'],
      ['$ cronduit signal --send',                   '> 1,247 builders notified.',       '> Open rate 41.2%.']
    ];
    let i = 0;
    function paint() {
      const set = lines[i % lines.length];
      term.innerHTML =
        '<div class="cp-term-line">' + set[0] + '<span class="cp-term-caret"></span></div>' +
        '<div class="cp-term-line cp-term-out">' + set[1] + '</div>' +
        '<div class="cp-term-line cp-term-out">' + set[2] + '</div>';
      i++;
    }
    paint();
    setInterval(paint, 3800);
  }

  /* ─── Boot ─────────────────────────────────────────────────────────────── */
  function boot() {
    startCounters();
    startActivityFeed();
    startReceiptStream();
    startTerminalRotator();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose for debugging / future hookups
  window.CronduitTrust = {
    computeBuilders: computeBuilders,
    computeShipped: computeShipped,
    computeOrdersToday: computeOrdersToday,
    computeViewing: computeViewing,
    relTimeFromAge: relTimeFromAge,
    EPOCH: EPOCH
  };
})();
